import { ImageType, isAvailable } from "../../../types/baseTypes";
import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import { Header_Kataloge } from "@/wp/types/acf-header-types";
import { UniversalHeader } from "../UniversalHeader/UniversalHeader";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Post } from "@/wp/types/client";
import { ParentsType } from "@/wp/getParentPages";
import { Content } from "@/components/Content/Content";
import { useRouter } from "next/router";
import { getVideoData } from "@/getData/getVideoData";
import { useState, useEffect } from "react";
import { HeaderVideoPlayer } from "../HeaderVideoPlayer/HeaderVideoPlayer";
import { MediaItemResponsive } from "@/components/MediaItem/MediaItemResponsive";
import { Headline } from "@/components/Headline/Headline";

export interface CatalogHeaderProps {
  post: Post<"catalog">;
  headerOptions?: HeadersOptionsType["header_catalog"];
  parents: ParentsType;
}

export const isAnyImageAvailable = (
  post: Header_Kataloge["header_catalog"],
): boolean => {
  if (!post?.media) {
    return false;
  }
  return (
    isAvailable<ImageType>(post.media.image_fullhd) ||
    isAvailable<ImageType>(post.media.image_desktop) ||
    isAvailable<ImageType>(post.media.image_tablet) ||
    isAvailable<ImageType>(post.media.image_mobile)
  );
};

export const CatalogHeader = ({
  post,
  headerOptions,
  parents,
}: CatalogHeaderProps) => {
  const header = post.acf.header_catalog;
  const router = useRouter();

  const {
    overline,
    subline,
    media,
    type,
    background_color_class,
    section_margin_class,
    link,
    link_extended,
    content,
    headline_text_align_class,
    // row_direction_class,
    seo_settings: { breadcrumb_title } = {},
  } = header ?? {};
  const {
    show_overline_subline,
    textheader,
    splitheader,
    show_extended_link,
    breadcrumb_position,
  } = headerOptions ?? {};
  const { text_width: textheaderTextWidth } = textheader ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
  } = splitheader ?? {};

  /* jscpd:ignore-start */
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );
  /* jscpd:ignore-end */

  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);
  useEffect(() => {
    if (!media || media.type !== "video") return;
    const videoId = "video" in media ? media.video?.[0] : undefined;
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    fetchVideoData();

    //eslint-disable-next-line
  }, []);

  const mediaJSX =
    media && media.type === "image" ? (
      <MediaItemResponsive
        image_desktop={media.image_desktop}
        image_tablet={media.image_tablet}
        image_mobile={media.image_mobile}
        image_fullhd={media.image_fullhd}
        // mediaFormat={imageheaderMediaFormat}
        sizes="(max-width: 767px) 50vw, 100vw"
        priority={true}
        mediaFormat={
          header.type === "splitheader" ? splitheaderMediaFormat : undefined
        }
        className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
        figcaption={media.show_caption && media.caption ? media.caption : ""}
        caption_position={media.caption_position}
      />
    ) : media &&
      media.type === "video" &&
      videoData &&
      videoData.acf.header_videos.video_type === "upload" ? (
      <HeaderVideoPlayer
        video={videoData.acf.header_videos}
        autoplay={media.autoplay}
        singlePageUrl={videoData.link}
      />
    ) : (
      <></>
    );

  const contentJSX = (
    <>
      <Headline
        headline={header?.title || ""}
        headline_html_tag="h1-pages"
        extraClass={`is-flex is-flex-direction-column ${headline_text_align_class}`}
        overline={showOverline ? overline : ""}
        subline={
          router.query.keyword
            ? (router.query.keyword as string)
            : showSubline
              ? subline
              : ""
        }
        disableAnimation
      />
      <Content
        headline=""
        headline_html_tag="h2"
        headline_text_align_class={headline_text_align_class}
        text_align_class={headline_text_align_class}
        button_type="primary"
        text={content}
        link={!show_extended_link && type !== "splitheader" && link}
        link_extended={show_extended_link && link_extended}
        isHeader={true}
      />
    </>
  );

  const breadcrumbsJSX = (
    <Breadcrumbs
      parents={parents}
      // post={post}
      breadcrumbPositon={
        breadcrumb_position
          ? breadcrumb_position
          : "is-justify-content-flex-start"
      }
      breadcrumbTitle={breadcrumb_title}
    />
  );

  return (
    <UniversalHeader
      type={type}
      mediaJSX={mediaJSX}
      contentJSX={contentJSX}
      breadcrumbsJSX={breadcrumbsJSX}
      background_color_class={background_color_class}
      section_margin_class={section_margin_class}
      textheaderTextWidth={textheaderTextWidth}
      link={link}
    />
  );
};
